import * as React from 'react';

import { FilterBox, type ICompactChip, type IDataColumn } from '@/design';
import type { IListCriteriaGroup, IListQuery, IListRow } from '../../../api/lists-contracts';
import { SignalChips } from '../../../components/state-cards';
import { rootChips, withoutNodes, type IFilterChip } from '../../../list-criteria';
import { ParamChip } from '../../sec-adv/firm-filters';
import { AdvancedFilterSheet, builderFields } from '../advanced-filter';
import { CellValue } from '../cells';
import { Chip, FiltersButton } from '../filter-chips';
import { Funnel, hasFunnel, type FacetsRead } from '../funnel';
import { Header } from '../grid';
import { signalGroupOf, signalsOn, withSignal, type IWorkSignal } from './signals';
import type { IWorkPage } from './use-work-page';

/**
 * The Lists controls drawn over a work page's design `DataTable` (SR-456): Lists' own header — the
 * label's press sorts, its tooltip says the source, the funnel filters — the extra columns, the
 * filter chips whose press opens their column's funnel, and *Advanced…*. A column the page derives
 * on the client ("Next step", "Disclosures") keeps a plain header: nothing on the server sorts or
 * filters it.
 */

/** What a section's funnels count under: its read's query, and the page's own conditions — tab,
 *  quick view, search — ANDed with the tree's. */
export interface IWorkScope {
  query: IListQuery;
  base: Nullable<IListCriteriaGroup>;
  /** A page whose rows are not Lists' own read — Metrics updates — reads its values there. */
  facets?: FacetsRead;
}

/** A field-backed column's header, under the page's own label; a code the catalogue lacks, the
 *  label alone. Never dragged — a work page has no group strip. */
export function WorkHeader({
  page,
  scope,
  code,
  label
}: {
  page: IWorkPage;
  scope: IWorkScope;
  code: string;
  label?: string;
}) {
  const field = page.fieldOf(code);
  if (!field) return <>{label ?? code}</>;
  // The page's label replaces the catalogue's whole, its question number too.
  const shown = { ...field, groupable: false, ...(label === undefined ? {} : { label, number: null }) };
  const open = page.funnel?.code === code;
  return (
    <Header
      field={shown}
      source={page.sourceOf(code)}
      sorts={page.sorts}
      onSort={page.onSort}
      funnel={
        <Funnel
          field={shown}
          root={page.root}
          query={scope.query}
          base={scope.base}
          facets={scope.facets}
          kindOf={page.kindOf}
          words={page.words}
          onApply={page.setRoot}
          onAdvanced={() => page.setAdvanced({ open: true })}
          open={open}
          onOpenChange={(next) => page.setFunnel(next ? { code, returnTo: null } : null)}
          returnTo={open ? (page.funnel?.returnTo ?? null) : null}
        />
      }
    />
  );
}

/** `(code, label)` → a column's `label` and kit `header`, to spread into an `IDataColumn`. */
export function workHeads(page: IWorkPage, scope: IWorkScope) {
  return (code: string, label: string) => ({
    label,
    header: <WorkHeader page={page} scope={scope} code={code} label={label} />
  });
}

/** Catalogue fields drawn as extra columns, each cell as Lists draws it. */
export function extraColumns(page: IWorkPage, scope: IWorkScope, codes: readonly string[]): IDataColumn<IListRow>[] {
  return codes.flatMap((code) => {
    const field = page.fieldOf(code);
    if (!field) return [];
    return [
      {
        id: code,
        label: field.label,
        header: <WorkHeader page={page} scope={scope} code={code} />,
        numeric: field.kind === 'number' || field.kind === 'money' || field.kind === 'percent',
        className: 'whitespace-nowrap',
        cell: (row: IListRow) => <CellValue field={field} cell={row.cells[code]} look={null} />
      }
    ];
  });
}

/** A chip's press: its column's funnel where a header draws one — a second press closes it — else
 *  *Advanced…* on its node: a column not drawn, hidden, or one that filters nothing. */
function pressChipOf(page: IWorkPage) {
  return (chip: IFilterChip, from: HTMLElement) => {
    const code = chip.field;
    const field = code === null ? undefined : page.fieldOf(code);
    const drawn = code !== null && document.querySelector(`[data-funnel="${CSS.escape(code)}"]`) !== null;
    if (chip.door === 'funnel' && code !== null && field && hasFunnel(field) && drawn) {
      page.setFunnel(page.funnel?.code === code ? null : { code, returnTo: from });
    } else page.setAdvanced({ open: true, focusId: chip.nodeIds[0] });
  };
}

/** *Advanced…*'s sheet, for a page whose *Filters* menu opens it (`WorkFilters`). */
export function WorkAdvancedSheet({ page }: { page: IWorkPage }) {
  return (
    <AdvancedFilterSheet
      open={page.advanced.open}
      onOpenChange={(open) => page.setAdvanced({ ...page.advanced, open, addField: undefined })}
      root={page.root}
      catalogue={page.catalogue}
      words={page.words}
      accounts={page.accounts}
      focusId={page.advanced.focusId}
      addField={page.advanced.addField}
      onApply={page.setRoot}
    />
  );
}

/**
 * A list-backed page's one *Filters* menu (SR-530), Lists' door (`FiltersButton`): its quick filters
 * where the toolbar folded them (always in the compact bar), a search over every field the list
 * filters on — one chosen opens its column's funnel where the table draws it, else *Advanced…* with a
 * condition on it — then *Advanced…* and *Clear all (n)*. `count` and `onClearAll` are the page's
 * applied filters', as its chip line counts and clears them; the open state is the page's, so a quick
 * filter's chip opens its control inside the menu (`useWorkToolbar`'s `openQuick`).
 */
export function WorkFilters({
  page,
  quick,
  count,
  onClearAll,
  open,
  onOpenChange
}: {
  page: IWorkPage;
  quick?: React.ReactNode;
  count: number;
  onClearAll: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const sections = React.useMemo(() => builderFields(page.catalogue).sections, [page.catalogue]);
  const onField = (code: string, from: HTMLElement) => {
    const field = page.fieldOf(code);
    const drawn = document.querySelector(`[data-funnel="${CSS.escape(code)}"]`) !== null;
    if (field && hasFunnel(field) && drawn) page.setFunnel({ code, returnTo: from });
    else page.setAdvanced({ open: true, addField: code });
  };
  return (
    <FiltersButton
      root={page.root}
      words={page.words}
      sections={sections}
      onField={onField}
      onAdvanced={() => page.setAdvanced({ open: true })}
      onChange={page.setRoot}
      quick={quick}
      count={count}
      onClearAll={onClearAll}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}

/**
 * The filter applied, as the applied filters' row draws it (`CompactChipLine`, under the toolbar and
 * on the compact bar's second row): the card signals on (`signals.ts`) lead, by name, as one
 * `SignalChips` — the rows of any of them — then the page's own (`own`: its quick filters, each with
 * its value), then Lists' chips, whose press opens their column's funnel, else *Advanced…* on their
 * node. Nothing while nothing narrows: the page draws no row then.
 */
export function workFilterChips(
  page: IWorkPage,
  signals: readonly IWorkSignal[] = [],
  own: readonly ICompactChip[] = []
): ICompactChip[] {
  const on = signalsOn(page, signals);
  const group = signalGroupOf(page, signals);
  const press = pressChipOf(page);
  const lists = rootChips(page.root, page.words).filter(
    (chip) => !group || !chip.nodeIds.every((id) => id === group.id)
  );
  return [
    ...(on.length > 0
      ? [
          {
            key: 'signals',
            chip: (
              <SignalChips
                chips={on.map((signal) => ({
                  key: signal.key,
                  words: signal.words,
                  clearName: `Show every row, not only ${signal.words}`,
                  onClear: () => page.setRoot(withSignal(page, signals, signal.leaves, false))
                }))}
              />
            )
          }
        ]
      : []),
    ...own,
    ...lists.map((chip) => ({
      key: `lists:${chip.key}`,
      chip: (
        <Chip
          chip={chip}
          invalid={false}
          onPress={(from) => press(chip, from)}
          onRemove={() => page.setRoot(withoutNodes(page.root, chip.nodeIds))}
        />
      )
    }))
  ];
}

/** A quick filter set, as a chip of the applied filters' row: its name and its value; its press
 *  opens its control (`open`, by the control's name — `useWorkToolbar`'s `openQuick` in the page,
 *  the compact bar's *Filters* there), its ✕ clears it. */
export function quickChip(
  label: string,
  said: string,
  open: (label: string) => true,
  onClear: () => void
): ICompactChip {
  return {
    key: `quick:${label}`,
    chip: <ParamChip words={`${label}: ${said}`} body={null} onPress={() => open(label)} onRemove={onClear} />
  };
}

/** A list-backed page's search in its toolbar: the filter box, with no *Filter* button — the page's
 *  search is live (`useLiveSearch`: a moment after the typing stops, at once on Enter), a search
 *  over the worklist on the server. */
export function WorkSearch({ search }: { search: IWorkSearchBox }) {
  return (
    <form
      role="search"
      className="flex min-w-0 flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        search.onSubmit();
      }}
    >
      <FilterBox
        label={search.label}
        placeholder={search.placeholder}
        value={search.draft}
        onChange={search.onDraft}
        className="flex-1"
      />
    </form>
  );
}

/** A page's search box: its names, and the draft its page and compact bar share. */
export interface IWorkSearchBox {
  label: string;
  placeholder: string;
  draft: string;
  onDraft: (draft: string) => void;
  onSubmit: () => void;
}
